/**
 * 現在の日付を取得する
 * ブラウザ実行環境のタイムゾーン差異を避けるため、JST に寄せた Date を返す。
 * @returns 日付
 */
export const getDate = () => {
  const date = new Date();
  return new Date(date.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' }));
};

/**
 * 年リストを取得する
 * ※1900～現在年
 *
 * @returns 年リスト
 */
const getBirthDateYearList = () => {
  const birthDateYearList = [];

  // 現在年から逆順で並べ、UI 側で最近の年を選びやすくする。
  for (let i = new Date().getFullYear(); i >= 1900; i -= 1) {
    birthDateYearList.push({
      key: i,
      value: `${i}`,
      label: `${i}`,
    });
  }
  return birthDateYearList;
};
export { getBirthDateYearList };

/**
 * 月リストを取得する
 *
 * @returns 月リスト
 */
const getBirthDateMonthList = () => {
  const birthDateMonthList = [];

  for (let month = 1; month <= 12; month += 1) {
    birthDateMonthList.push(String(month));
  }

  return birthDateMonthList;
};
export { getBirthDateMonthList };

/**
 * 日リストを取得する
 *
 * @param {string | undefined} year 年
 * @param {string | undefined} month 月
 * @returns 日リスト
 */
const getBirthDateDayList = (year: string | undefined, month: string | undefined) => {
  const birthDateDayList = [];
  let lastDay = new Date(Number(year), Number(month), 0).getDate();

  // 年月未確定時でも日リストが空になりすぎないよう、31日で暫定表示する。
  if (!lastDay) lastDay = 31;

  for (let i = 1; i <= lastDay; i += 1) {
    birthDateDayList.push({
      key: i,
      value: `${i}`,
      label: `${i}`,
    });
  }

  return birthDateDayList;
};
export { getBirthDateDayList };

/**
 * 生年月日が存在するかどうか
 *
 * @param {string} year 年
 * @param {string} month 月
 * @param {string} day 日
 * @returns {boolean} 生年月日が存在する場合はtrue
 */
const existsBirthDate = (
  year: string | undefined,
  month: string | undefined,
  day: string | undefined
): boolean => {
  const y = Number(year);
  const m = Number(month);
  const d = Number(day);
  const dt = new Date(y, m - 1, d);

  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
};
export { existsBirthDate };

/**
 * ゼロ埋めする
 *
 * @param {number} num 数値
 * @param {number} digit 桁数
 * @returns ゼロ埋めされた文字列
 */
const zeroPadding = (num: number, digit: number) => {
  return `00${num}`.slice(digit * -1);
};
export { zeroPadding };

/**
 * 対象年月リストを取得する
 *
 * @param {string | undefined} targetYearMonth 対象年月
 * @returns 対象年月リスト
 */
const getTargetYearMonthList = (targetYearMonth: string | undefined) => {
  const targetYearMonthList = [];
  const dateObj = new Date();
  let month = dateObj.getUTCMonth() + 1;
  let year = dateObj.getUTCFullYear();

  if (targetYearMonth !== null && targetYearMonth !== '') {
    // 指定年月があればそこを起点にし、請求明細などの前回選択を維持する。
    year = Number(targetYearMonth?.substring(0, 4));
    month = Number(targetYearMonth?.substring(5, 7));
  }

  for (let i = 0; i < 24; i += 1) {
    if (month <= 0) {
      year -= 1;
      month += 12;
    }
    let newDate = '';
    if (month < 10) {
      newDate = `${year}/0${month.toString()}`;
    } else {
      newDate = `${year}/${month}`;
    }

    targetYearMonthList.push({
      key: newDate,
      value: `${newDate}`,
      label: `${newDate}`,
    });
    month -= 1;
  }
  return targetYearMonthList;
};
export { getTargetYearMonthList };

export type DateListTypes = Array<DateListType>;

export type DateListType = {
  date: string;
  year: string;
  month: string;
  day: string;
  weekday: string;
};
/**
 * 日付情報を生成
 * @param {Date} currentDate
 * @returns {DateListTypes}
 */
const generateDateInfo = (currentDate: Date) => {
  // 文字列比較や API 送信用に使いやすいよう、YYYYMMDD と分解済み要素を同時に返す。
  // 文字列に変換
  const year = currentDate.getFullYear().toString();
  const month = (currentDate.getMonth() + 1).toString().padStart(2, '0');
  const day = currentDate.getDate().toString().padStart(2, '0');

  // 曜日情報取得
  const weekdayIndex = currentDate.getDay();
  const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
  const weekday = weekdays[weekdayIndex];

  return { date: year + month + day, year, month, day, weekday };
};

/**
 * 年月日曜日リストを取得する
 *
 * @param {string} startDate 開始日付 20234011
 * @param {string} endDate 終了日付 20235011
 * @returns 年月日曜日リスト
 */
const getRangeDateInfo = (startDate: string, endDate: string): DateListTypes => {
  const dateRangeInfoList: DateListTypes = [];

  // 開始日と終了日から年・月・日を抽出
  const startYear = Number(startDate.slice(0, 4));
  const startMonth = Number(startDate.slice(4, 6));
  const startDay = Number(startDate.slice(6, 8));
  const endYear = Number(endDate.slice(0, 4));
  const endMonth = Number(endDate.slice(4, 6));
  const endDay = Number(endDate.slice(6, 8));

  // 開始日からループ開始
  const currentDate = new Date(startYear, startMonth - 1, startDay);

  while (currentDate <= new Date(endYear, endMonth - 1, endDay)) {
    dateRangeInfoList.push(generateDateInfo(currentDate));

    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dateRangeInfoList;
};
export { getRangeDateInfo, generateDateInfo };

/**
 * ８桁の日付文字を、”YY-MM-DD”形式に変換する
 * @param date
 * @returns
 */
const formatDateToYYMMDD = (date: string): string => {
  const year = date.slice(0, 4);
  const month = date.slice(4, 6);
  const day = date.slice(6, 8);
  return `${year}-${month}-${day}`;
};
export { formatDateToYYMMDD };

/**
 * ”YY-MM-DD”形式を、８桁の日付文字に変換する
 * @param date
 * @returns
 */
const parseYYYYMMDDtoDate = (date: string): string => {
  const dateParts = date.split('-');
  const year = dateParts[0];
  const month = dateParts[1];
  const day = dateParts[2];

  return `${year}${month}${day}`;
};

export { parseYYYYMMDDtoDate };

/**
 * YYYYMM形式の年月から、年、月を分けて取得する
 * @param yyyymm
 * @returns year, month
 */
export const splitYearMonthFromYYYYMM = (yyyymm: string) => {
  const invalidResponse = { year: '', month: '' };

  // 想定外フォーマット時は空文字を返し、呼び出し側で安全に非表示へ寄せられるようにする。
  if (!yyyymm) return invalidResponse;

  if (yyyymm.length !== 6) return invalidResponse;

  const year = yyyymm.slice(0, 4);
  const month = yyyymm.slice(4);

  return { year, month };
};

/**
 * 「日付（ISO形式）」の年月日を取得する
 * @param date
 * @returns year：年, month：月, day：日
 */
export const getIsoFormatDate = (date: string) => {
  const parts = date.split('-');
  if (parts.length < 2) {
    return {
      year: '',
      month: '',
      day: '',
    };
  }
  return {
    year: parts[0],
    month: parts[1],
    day: parts[2],
  };
};

/**
 * 指定した日付が月末かどうかを判定する
 * @param {Date} date 判定対象の日付。省略した場合は現在の日付を使用する
 * @returns {boolean} trueの場合は、月末。falseの場合は、それ以外
 */
export const isLastDayOfMonth = (date: Date = getDate()): boolean => {
  const tomorrow = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
  return tomorrow.getDate() === 1;
};
